import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  createStyles,
  Divider,
  List,
  ListItem,
  ListItemText,
  makeStyles,
  Typography
} from '@material-ui/core';
import React from 'react';
import { StudentContext } from '../../contexts';
import * as DTO from '../../dto';
import { useSiteMTSActivityTypes } from '../../hooks';
import { LanguageString } from '../Common';

type AssignedActivitySummary = {
  activityTypeId: number;
  hours: number | null;
  activityType?: DTO.MTSActivityType;
};

const useStyles = makeStyles(theme =>
  createStyles({
    card: {
      height: '100%'
    },
    cardHeader: {
      paddingBottom: 0,
      backgroundColor: theme.palette.primary.light,
      color: theme.palette.primary.contrastText
    },
    summaryRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: theme.spacing(2),
      marginBottom: theme.spacing(2)
    },
    emptyState: {
      color: theme.palette.text.secondary
    },
    listItem: {
      paddingLeft: 0,
      paddingRight: 0
    },
    hoursChip: {
      minWidth: 80,
      justifyContent: 'center'
    },
    totalHours: {
      fontWeight: theme.typography.fontWeightMedium
    },
    caption: {
      color: theme.palette.text.secondary
    }
  })
);

const formatHours = (hours: number) => {
  if (Number.isInteger(hours)) {
    return hours.toString();
  }

  return hours.toFixed(2).replace(/\.00$/, '').replace(/(\.\d)0$/, '$1');
};

export const AssignedActivitiesCard: React.FC = () => {
  const classes = useStyles();
  const studentCtx = React.useContext(StudentContext);
  const { data } = useSiteMTSActivityTypes({ variables: { getFilterPropValue: false } });

  const student = studentCtx?.student;
  const activityTypes = data?.site.mtsActivityTypes ?? [];

  const assignedActivities = React.useMemo<AssignedActivitySummary[]>(() => {
    const rawAssignments = student?.assignedActivityIds ?? [];

    return rawAssignments
      .map((assignment): AssignedActivitySummary | null => {
        if (typeof assignment === 'number') {
          return {
            activityTypeId: assignment,
            hours: null,
            activityType: activityTypes.find(type => type.id === assignment)
          };
        }

        if (assignment?.mtsActivityId == null) {
          return null;
        }

        return {
          activityTypeId: assignment.mtsActivityId,
          hours: assignment.hours ?? null,
          activityType: activityTypes.find(type => type.id === assignment.mtsActivityId)
        };
      })
      .filter((assignment): assignment is AssignedActivitySummary => assignment != null)
      .sort((left, right) => {
        const leftSortIndex = left.activityType?.sortIndex ?? Number.MAX_SAFE_INTEGER;
        const rightSortIndex = right.activityType?.sortIndex ?? Number.MAX_SAFE_INTEGER;

        if (leftSortIndex !== rightSortIndex) {
          return leftSortIndex - rightSortIndex;
        }

        return left.activityTypeId - right.activityTypeId;
      });
  }, [activityTypes, student]);

  const totalAssignedHours = React.useMemo(
    () => assignedActivities.reduce((total, activity) => total + (activity.hours ?? 0), 0),
    [assignedActivities]
  );

  return assignedActivities.length > 0 ? (
    <Card classes={{ root: 'assigned-activities-card' }} className={classes.card}>
      <CardHeader
        className={classes.cardHeader}
        title={<LanguageString groupName="MTS" resourceName="ASSIGNED_ACTIVITIES" defaultText="Assigned Activities" />}
        subheader={
          <LanguageString groupName="MTS" resourceName="ASSIGNED_ACTIVITIES_SUBHEADER" defaultText="Configured activity types and assigned hours for this student." />
        }
      />
      <CardContent>
        <Box className={classes.summaryRow}>
          <Box>
            <Typography variant="body2" className={classes.caption}>
              <LanguageString groupName="MTS" resourceName="ASSIGNED_ACTIVITY_COUNT" defaultText="Assigned activity types" />
            </Typography>
            <Typography variant="h5">{assignedActivities.length}</Typography>
          </Box>
          <Chip
            color="primary"
            className={classes.hoursChip}
            label={
              <>
                {formatHours(totalAssignedHours)}{' '}
                <LanguageString groupName="MTS" resourceName="HOURS" defaultText="Hours" />
              </>
            }
          />
        </Box>

        <Divider />

        {assignedActivities.length > 0 ? (
          <List disablePadding>
            {assignedActivities.map(activity => (
              <React.Fragment key={activity.activityTypeId}>
                <ListItem className={classes.listItem}>
                  <ListItemText
                    primary={
                      activity.activityType ? (
                        <LanguageString languageString={activity.activityType.name} />
                      ) : (
                        <Typography variant="body1">
                          <LanguageString groupName="MTS" resourceName="UNKNOWN_ACTIVITY_TYPE" defaultText="Unknown activity type" />{' '}
                          #{activity.activityTypeId}
                        </Typography>
                      )
                    }
                    secondary={
                      activity.activityType?.description ? (
                        <Typography variant="body2" color="textSecondary">
                          {activity.activityType.description}
                        </Typography>
                      ) : undefined
                    }
                  />
                  {activity.hours != null &&
                    <Typography variant="body2" className={classes.totalHours}>
                      {formatHours(activity.hours)}{' '}
                      <LanguageString groupName="MTS" resourceName="HOURS" defaultText="Hours" />
                    </Typography>
                  }
                </ListItem>
                <Divider component="li" />
              </React.Fragment>
            ))}
          </List>
        ) : (
          <Box pt={2}>
            <Typography variant="body2" className={classes.emptyState}>
              <LanguageString groupName="MTS" resourceName="NO_ASSIGNED_ACTIVITIES" defaultText="No MTS activity types are assigned to this student." />
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  ) : null;
};
